import { createColumnHelper } from '@tanstack/react-table';
import { OrderListColumns } from '@ldt/features/orders/order_list/shared';
import type { OrderListSearchConfig } from 'src/distribution_platforms/features/order_list';
import { getOrderPatientFullName } from 'src/distribution_platforms/features/utils/patient_utils';
import type { WorkflowDefinition } from 'src/distribution_platforms/features/workflow_definition';
import { toOrderListColumns } from 'src/distribution_platforms/features/workflow_definition';
import {
  LdtOrderListItemFieldsFragment,
  SortBy,
  SpecimenType,
} from 'src/types';
import { SpecimenTypeUtils } from 'src/utils';

export const orderListSearchConfig: OrderListSearchConfig = {
  placeholder:
    'Search by Tempus ID (OHID), Order ID, Patient, or Physician Name',
  width: '480px',
};

/**
 * Order list columns for Tempus workflow.
 */
export function getOrderListColumns(workflow: WorkflowDefinition) {
  const columnHelper = createColumnHelper<LdtOrderListItemFieldsFragment>();
  const hasMultipleSpecimenTypes =
    SpecimenTypeUtils.hasMultipleSpecimenTypesEnabled(
      workflow.supportedSpecimenTypes,
    );

  return toOrderListColumns([
    columnHelper.accessor('externalId', {
      id: OrderListColumns.TempusId,
      header: 'TEMPUS ID (OHID)',
      minSize: 180,
      maxSize: 200,
      meta: {
        isPrimaryColor: true,
      },
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor(
      (order: LdtOrderListItemFieldsFragment) => order.crossOrgId,
      {
        id: OrderListColumns.Id,
        header: 'ORDER ID#',
        minSize: 160,
        maxSize: 180,
        meta: {
          isPrimaryColor: true,
        },
        cell: (info) => info.getValue(),
      },
    ),
    ...(hasMultipleSpecimenTypes
      ? [
          columnHelper.accessor(
            (order: LdtOrderListItemFieldsFragment) => order.specimenType,
            {
              id: OrderListColumns.Type,
              header: 'SPECIMEN TYPE',
              minSize: 220,
              maxSize: 220,
              cell: (info) =>
                info.getValue()
                  ? SpecimenTypeUtils.getSpecimenTypeName(
                      info.getValue() as SpecimenType,
                    )
                  : '',
            },
          ),
        ]
      : []),
    columnHelper.accessor(
      (order: LdtOrderListItemFieldsFragment) => getOrderPatientFullName(order),
      {
        id: OrderListColumns.Patient,
        header: 'PATIENT',
        minSize: 220,
        maxSize: 260,
        size: 260,
        cell: (info) => info.getValue() || '',
      },
    ),
    columnHelper.accessor(
      (order: LdtOrderListItemFieldsFragment) => {
        const orderingProvider = order.orderingProvider;
        if (!orderingProvider?.orderingPhysician) return null;
        const op = orderingProvider.orderingPhysician;
        return [op.firstName, op.lastName].filter(Boolean).join(' ');
      },
      {
        id: OrderListColumns.OrderingPhysician,
        header: 'ORDERING PHYSICIAN',
        minSize: 220,
        maxSize: 260,
        size: 260,
        cell: (info) => info.getValue() || '',
      },
    ),
    columnHelper.accessor('createdAt', {
      id: OrderListColumns.SubmissionDate,
      header: 'SUBMISSION DATE',
      minSize: 200,
      maxSize: 200,
      enableSorting: true,
      sortingFn: 'datetime',
      meta: { sortBy: SortBy.SubmittedAt },
      cell: (info) =>
        info.getValue() ? new Date(info.getValue()!).toLocaleDateString() : '',
    }),
    columnHelper.accessor(
      (order: LdtOrderListItemFieldsFragment) => order.status,
      {
        id: OrderListColumns.Status,
        header: 'STATUS',
        minSize: 240,
        maxSize: 240,
        size: 240,
        enableSorting: false,
        cell: (info) => workflow.getStatusDisplayName(info.getValue()),
      },
    ),
  ]);
}
